// Shared nav-hijack helper used by TheNav.
// In horizontal mode, clicking an in-page hash link (e.g. "/#projects")
// should animate the horizontal track instead of doing a native vertical
// hash-scroll. Cross-page links (e.g. "/explore#blog") and mobile/vertical
// mode both fall through to normal NuxtLink/anchor navigation.
// The hash is how a destination gets expressed — it is what makes a cross-page
// link and a pasted deep link land in the right place. It is just noise once
// you have arrived, so it is wiped from the address bar after the fact.
//
// history.replaceState, not the router: this is cosmetic, and going through the
// router would count as a navigation and re-run scroll handling. It also leaves
// route.hash untouched, so it cannot re-trigger the watcher below.
//
// Module scope so useHorizontalScroll can call it too, once it has finished
// acting on a deep link.
export function clearUrlHash() {
  if (typeof window === 'undefined' || !window.location.hash) return;
  window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + window.location.search
  );
}

export function usePanelNav() {
  const state = useState('horizontalScroll', () => ({
    isHorizontal: false,
    scrollToPanel: null,
  }));
  const route = useRoute();

  // Taken here, during setup, and not inside the click handler below.
  // useNuxtApp() throws outside a Nuxt instance context, and a click handler
  // fires long after setup has finished: the throw left preventDefault done
  // but the smooth scroll never started, so the browser jumped instead.
  const { $lenis } = useNuxtApp();

  // Watching rather than clearing inline: preventDefault() runs after
  // RouterLink has already navigated, so at click time the hash is not in the
  // URL yet. The watcher fires once the router has actually written it.
  //
  // Deliberately not immediate. A hash present on first load is a deep link,
  // and useHorizontalScroll reads it during start() to decide where to scroll —
  // clearing it here on mount would race that read and break deep linking. That
  // one case clears itself after it has been acted on.
  watch(
    () => route.hash,
    (hash) => {
      if (hash) nextTick(clearUrlHash);
    }
  );

  function navigateToPanel(event, path) {
    const hashIndex = path.indexOf('#');
    if (hashIndex === -1) return;

    const targetPath = path.slice(0, hashIndex) || '/';
    const hash = path.slice(hashIndex + 1);
    const isSamePage = targetPath === route.path;

    // A different page still needs the hash, so it lands in the right place on
    // arrival. Only same-page jumps are worth intercepting.
    if (!isSamePage) return;

    const el = document.getElementById(hash);
    if (!el) return;

    if (state.value.isHorizontal && state.value.scrollToPanel) {
      event.preventDefault();
      state.value.scrollToPanel(el);
      return;
    }

    // Vertical mode, which on this site means a phone. Left alone, the browser
    // performs a native hash jump: instant, and jarring after a page that
    // eases everywhere else. Lenis animates it with the same easing the rest of
    // the scrolling uses.
    event.preventDefault();

    if ($lenis?.scrollTo) {
      $lenis.scrollTo(el, { duration: 1.1 });
      return;
    }

    // No Lenis, so ask the browser for the nicest thing it will do.
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return { navigateToPanel };
}
